import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Chart, ChartConfiguration, ChartType } from 'chart.js';

export interface ChartConfig {
    labelsList: string[];
    data: number[];
    displayLegend: boolean;
    chartTypeStr?: string;
    onClick?: (label: string, index: number) => void;
}

@Injectable({
    providedIn: 'root'
})
export class DashboardChartService {

    constructor(private router: Router) { }


    getColourArrayForChartData(items: any[]) {
        return DashboardChartService.colors.slice(0, items.length);
    }

    createPieChart(context: CanvasRenderingContext2D, config: ChartConfig): Chart {
        const chartConfig: ChartConfiguration = {
            type: (config.chartTypeStr || 'pie') as ChartType,
            data: {
                labels: config.labelsList,
                datasets: [
                    {
                        data: config.data,
                        backgroundColor: this.getColourArrayForChartData(config.labelsList),
                        radius: 94
                    }
                ]
            },
            options: {
                animation: false,

                layout: {
                    padding: {
                        top: 5
                    }
                },
                responsive: false,
                plugins: {
                    legend: {
                        display: config.displayLegend,
                        position: 'right',
                        labels: {
                            boxWidth: 10,
                        
                            generateLabels: function (chart) {
                                const original = Chart.overrides.pie.plugins.legend.labels.generateLabels;
                                const labels = original(chart);
                        
                                labels.forEach(label => {
                                    if (label.text.length > 10) {
                                        label.text = label.text.substring(0, 10) + '...';
                                    }
                                });
                        
                                return labels;
                            },
                        
                            filter: function (legendItem, chartData) {
                                const data = chartData.datasets[0].data;
                        
                                const sortedIndices = data
                                    .map((value, index) => ({
                                        index,
                                        value: typeof value === 'number' ? value : 0
                                    }))
                                    .sort((a, b) => b.value - a.value)
                                    .slice(0, 6)
                                    .map(item => item.index);
                        
                                return sortedIndices.includes(legendItem.index);
                            }
                        }
                    }
                },
                onClick: (e, elements) => {
                    if (config.onClick && elements && elements.length > 0) {
                        const selectedSegment = elements[0];
                        const selectedSegmentLabel = chartConfig.data.labels[selectedSegment.index] as string;
                        config.onClick(selectedSegmentLabel, selectedSegment.index);
                    }
                }
            }
        };

        return new Chart(context, chartConfig);
    }

    createBarChart(context: CanvasRenderingContext2D, config: ChartConfig): Chart {
        const chartConfig: ChartConfiguration = {
            type: (config.chartTypeStr || 'bar') as ChartType,
            data: {
                labels: config.labelsList,
                datasets: [
                    {
                        data: config.data,
                        backgroundColor: this.getColourArrayForChartData(config.labelsList)
                    }
                ]
            },
            options: {
                animation: false,
                responsive: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1
                        }
                    }
                },
                plugins: {
                    legend: {
                        position: 'bottom',
                        display: config.displayLegend,
                        labels: {
                            filter: function (legendItem, chartData) {
                                const data = chartData.datasets[0].data;

                                const sortedIndices = data
                                    .map((value, index) => ({
                                        index,
                                        value: typeof value === 'number' ? value : 0
                                    }))
                                    .sort((a, b) => b.value - a.value)
                                    .slice(0, 6)
                                    .map(item => item.index);

                                return sortedIndices.includes(legendItem.index);
                            }
                        }
                    }
                },
                onClick: (e, elements) => {
                    if (config.onClick && elements && elements.length > 0) {
                        const selectedSegment = elements[0];
                        const selectedSegmentLabel = chartConfig.data.labels[selectedSegment.index] as string;
                        config.onClick(selectedSegmentLabel, selectedSegment.index);
                    }
                }
            }
        };

        return new Chart(context, chartConfig);
    }


    private static colors = [
        "#DC385F", "#331D41", "#E86A84", "#F4A6B7",
        "#F9DCE2", "#5A3A62", "#6D4A75", "#8C6F98",
        "#B9A4C2", "#E8DFF0", "#4E6E62", "#5F8A76",
        "#7FAE9A", "#A3C7BA", "#D7E7E1", "#3B4F63",
        "#516A82", "#6E8FA7", "#A9C3D6", "#DCE8F0",
        "#6A9FA3", "#8BBBBC", "#C9E1E2"
      ];
}